import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import testHarness from '../../api/testHarness';

/**
 * Modal for rolling mock-SL availability against a single OPEN period.
 * Supports a stress toggle (heavy NO weighting) or manual weight sliders.
 */
export default function SimulateAvailabilityModal({ period, onClose, onDone }) {
  const [stress, setStress]       = useState(false);
  const [yesW,   setYesW]         = useState(0.70);
  const [noW,    setNoW]          = useState(0.15);
  const [maybeW, setMaybeW]       = useState(0.15);
  const [seed,   setSeed]         = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [error,  setError]        = useState('');

  const mutation = useMutation({
    mutationFn: testHarness.simulateAvailability,
    onSuccess: (data) => { onDone?.(data); onClose(); },
    onError:   (err)  => setError(err.data?.detail || err.message),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    const body = {
      period_id:  period.id,
      overwrite,
    };
    if (stress) {
      body.stress_mode = true;
    } else {
      body.weights = { yes: yesW, no: noW, maybe: maybeW };
    }
    if (seed !== '') body.rng_seed = parseInt(seed, 10);
    mutation.mutate(body);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl space-y-4"
      >
        <div>
          <h2 className="text-lg font-bold text-gray-900">Simulate Availability</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Fills in availability for every mock SL on period{' '}
            <span className="font-semibold">{period.start_date} → {period.end_date}</span>.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={stress} onChange={(e) => setStress(e.target.checked)} />
          Stress mode (heavy NO weighting — tests near-infeasibility)
        </label>

        {!stress && (
          <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs font-semibold text-gray-600 uppercase">Response weights</p>
            <Slider label="YES"   value={yesW}   onChange={setYesW}   color="green" />
            <Slider label="NO"    value={noW}    onChange={setNoW}    color="red" />
            <Slider label="MAYBE" value={maybeW} onChange={setMaybeW} color="yellow" />
            <p className="text-[11px] text-gray-500">
              Values are auto-normalized. The solver still receives responses clipped to the period's
              max-no caps.
            </p>
          </div>
        )}

        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">RNG seed (optional)</label>
            <input
              type="number"
              value={seed}
              onChange={(e) => setSeed(e.target.value)}
              placeholder="e.g. 42"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <label className="flex items-end gap-2 text-sm text-gray-700 pb-2">
            <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
            Overwrite existing
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-40"
          >
            {mutation.isPending ? 'Rolling…' : 'Run Simulation'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Slider({ label, value, onChange, color }) {
  const colors = { green: 'accent-green-600', red: 'accent-red-600', yellow: 'accent-yellow-500' };
  return (
    <div>
      <div className="flex justify-between text-xs font-medium text-gray-700 mb-1">
        <span>{label}</span>
        <span>{value.toFixed(2)}</span>
      </div>
      <input
        type="range"
        min="0" max="1" step="0.05"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className={`w-full ${colors[color]}`}
      />
    </div>
  );
}
